import { useState, useEffect } from "react";
import axios from "axios";

/**
 * Custom hook to fetch data using Axios.
 *
 * @template T
 * @param {Object} params - The parameters for the fetch.
 * @param {string} params.url - The URL to fetch data from.
 * @param {import("axios").AxiosInstance | import("axios").AxiosStatic} [params.client] - The Axios client instance.
 * @param {Partial<T>} [params.initialData] - Optional initial data of partial type `T`.
 * @param {import("axios").AxiosRequestConfig} [params.options] - Additional Axios request options.
 *
 * @returns {{data: T | null, isLoading: boolean, error: Object | null}} The fetch result including data of type `T`, loading state, and error information.
 */
export const useFetch = ({
  url,
  client = axios,
  initialData = null,
  options = {},
}) => {
  const [data, setData] = useState(initialData);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await client.get(url, options);
        setData(response.data);
      } catch (err) {
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  return {
    data,
    isLoading,
    error,
  };
};
